import { Post } from '../../types/post';

export function fullscreenView() {
  return {
    fullscreenPost: null as Post | null,
    touchStartY: 0,
    touchDeltaY: 0,
    clipsPosts: [],

    showClipsPageLink: false,
    showShareTooltip: false,
    shareTooltipTimer: null as number | null,

    recentUserPosts: [] as Post[],
    isLoadingUserPosts: false,

    // move this to my compilations page function
    showExportModal: false,
    isExporting: false,

    async initFullscreenViewFromURL() {
      // Initialize fullscreen state from URL if needed
      const params = new URLSearchParams(window.location.search);
      const postId = params.get('post');
      if (postId) {
        const post = await this.redditService.fetchPostById(postId);
        if (post) {
          this.fullscreenPost = post;
          this.lockBody()
          this.recentUserPosts = await this.redditService.fetchUserPosts(post.data.author);
        }
      } else {
        this.fullscreenPost = null;
        this.unlockBody()
      }
    },

    async loadUserPosts(username: string) {
      if (!username) return;

      this.isLoadingUserPosts = true;
      this.recentUserPosts = [];

      setTimeout(() => {
        this.isLoadingUserPosts = false;
      }, 2000)

      try {
        const response = await fetch(`https://api.reddit.com/user/${username}/submitted?limit=10`);
        const data = await response.json();

        // Filter posts to only include ones with media content
        const userPosts = data.data.children.filter((post: Post) =>
          post.data.preview?.reddit_video_preview ||
          post.data.preview?.images?.[0] ||
          post.data.secure_media_embed?.content
        );
        this.recentUserPosts = userPosts

      } catch (error) {
        console.error('Error fetching user posts:', error);
      } finally {
        this.isLoadingUserPosts = false;
      }
    },

    lockBody() {
      // Prevent body scrolling
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      // Store current scroll position
      document.body.dataset.scrollY = window.scrollY.toString();
    },

    async openFullscreen(post: Post) {
      // Reset any lingering transform styles
      const container = document.querySelector('.fullscreen-container');
      if (container) {
        container.style.transform = '';
      }

      this.showClipsPageLink = false
      this.fullscreenPost = post;
      this.updateURL(post);

      document.getElementById('fullscreen-content')?.scroll({ top: 0 })
      this.lockBody()

      this.isLoadingUserPosts = true;
      try {
        this.recentUserPosts = await this.redditService.fetchUserPosts(post.data.author);
      } catch (error) {
        console.error('Error loading user posts:', error);
      } finally {
        this.isLoadingUserPosts = false;
      }
    },

    unlockBody() {
      // Restore body scrolling
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    },

    closeFullscreen(container) {
      // Reset transform and state
      if (container) {
        container.style.transform = '';
      }
      this.touchStartY = 0;
      this.touchDeltaY = 0;
      this.fullscreenPost = null;
      this.updateURL(null);

      this.unlockBody()
      // Restore scroll position
      const scrollY = parseInt(document.body.dataset.scrollY || '0');
      window.scrollTo(0, scrollY);
    },

    updateURL(post: Post | null) {
      const params = new URLSearchParams(window.location.search);

      if (post) {
        // Set the post ID and remove the subreddit parameter
        params.set('post', post.data.id);
        params.delete('sr');
      } else {
        // When closing fullscreen, restore the subreddit parameter
        params.delete('post');
        if (this.subreddit) {
          params.set('sr', this.subreddit);
        }
      }

      // Construct the new URL, only including query string if there are parameters
      const queryString = params.toString();
      const newUrl = `${window.location.pathname}${queryString ? '?' + queryString : ''}`;

      if (newUrl !== window.location.href) {
        window.history.pushState({}, '', newUrl);
      }
    },

    handleTouchStart(e: TouchEvent) {
      this.touchStartY = e.touches[0].clientY;
      this.touchDeltaY = 0;
    },

    handleTouchMove(e: TouchEvent) {
      if (!this.touchStartY) return;

      // dont start swipe down unless were at top of content
      const contentScroll = document.getElementById('fullscreen-content')?.scrollTop
      if (contentScroll) {
        return
      }

      this.touchDeltaY = e.touches[0].clientY - this.touchStartY;
      if (this.touchDeltaY > 0) {
        e.preventDefault();
        const container = e.currentTarget as HTMLElement;
        container.style.transform = `translateY(${this.touchDeltaY}px)`;
      }
    },

    handleTouchEnd(e: TouchEvent) {
      const container = e.currentTarget as HTMLElement;
      if (this.touchDeltaY > 100) {
        this.closeFullscreen(container);
      } else {
        container.style.transform = 'translateY(0)';
      }
      this.touchStartY = 0;
      this.touchDeltaY = 0;
    },

    addToClips() {
      if (!this.fullscreenPost) {
        return
      }

      const alreadyClipped = this.clipsPosts.some(p => p.data.id === this.fullscreenPost.data.id)

      if (!alreadyClipped) {
        this.clipsPosts = [this.fullscreenPost, ...this.clipsPosts];
        localStorage.setItem('compilationPosts', JSON.stringify(this.clipsPosts));

        // Show success message
        this.showClipsPageLink = true;
      }

      // Track clipping if on prod
      if (window.location.origin === 'meatscroller.com' && 'umami' in window) {
        (window as any).umami.track('clipped', {
          commentId: this.fullscreenPost?.data.id,
        });
      }
    },

    downloadMedia() {
      if (!this.fullscreenPost) {
        console.error('No post selected');
        return;
      }

      const post = this.fullscreenPost;
      let mediaUrl: string;
      let fileExtension: string;
      let mimeType: string;

      // Determine the media type and URL
      if (this.hasVideo(post)) {
        mediaUrl = this.getVideoUrl(post);
        fileExtension = 'mp4';
        mimeType = 'video/mp4';
      } else if (this.hasImage(post)) {
        mediaUrl = this.getImageUrl(post);
        // Get extension from URL or default to jpg
        fileExtension = mediaUrl.split('.').pop()?.toLowerCase().split('?')[0] || 'jpg';
        // Handle different image types
        switch (fileExtension) {
          case 'gif':
            mimeType = 'image/gif';
            break;
          case 'png':
            mimeType = 'image/png';
            break;
          case 'webp':
            mimeType = 'image/webp';
            break;
          default:
            fileExtension = 'jpg';
            mimeType = 'image/jpeg';
        }
      } else if (this.hasEmbed(post)) {
        // Extract the iframe src from embedded content
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = this.unescapeHTML(post.data.secure_media_embed.content);
        const iframeSrc = tempDiv.querySelector('iframe')?.src;

        if (!iframeSrc) {
          console.error('Could not extract media URL from embed');
          return;
        }

        mediaUrl = iframeSrc;
        fileExtension = 'mp4';
        mimeType = 'video/mp4';
      } else {
        console.error('No downloadable media found');
        return;
      }

      // Clean up the filename
      const filename = this.sanitizeFileName(post.data.title || 'download');
      const fullFilename = `${filename}.${fileExtension}`;

      // Show loading state (you'll need to add UI elements for this)
      const startDownload = () => {
        fetch(mediaUrl)
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.blob();
          })
          .then(blob => {
            // Create a new blob with the correct MIME type
            const mediaBlob = new Blob([blob], { type: mimeType });
            const blobUrl = URL.createObjectURL(mediaBlob);

            // Create a temporary download link
            const downloadLink = document.createElement('a');
            downloadLink.style.display = 'none';
            downloadLink.href = blobUrl;
            downloadLink.download = fullFilename;

            // Trigger download
            document.body.appendChild(downloadLink);
            downloadLink.click();

            // Cleanup
            setTimeout(() => {
              document.body.removeChild(downloadLink);
              URL.revokeObjectURL(blobUrl);
            }, 100);
          })
          .catch(error => {
            console.error('Download failed:', error);
            // Handle error - you might want to show an error message to the user
          });
      };

      // Handle cross-origin issues for Reddit media
      if (mediaUrl.includes('redd.it') || mediaUrl.includes('reddit.com')) {
        // Reddit media requires removing the '&amp;' encoding
        mediaUrl = mediaUrl.replace(/&amp;/g, '&');
      }

      // Start the download
      startDownload();
    },

    loadCompilationPosts() {
      const saved = localStorage.getItem('compilationPosts');
      if (saved) {
        this.clipsPosts = JSON.parse(saved);
      }
    },

    removeFromCompilation(post: Post) {
      const index = this.clipsPosts.findIndex(p => p.data.id === post.data.id);
      if (index !== -1) {
        this.clipsPosts.splice(index, 1);
        localStorage.setItem('compilationPosts', JSON.stringify(this.clipsPosts));
      }
    },

    shareContent() {
      const url = window.location.href;
      navigator.clipboard.writeText(url).then(() => {
        this.showShareTooltip = true;
        if (this.shareTooltipTimer) {
          clearTimeout(this.shareTooltipTimer);
        }
        this.shareTooltipTimer = setTimeout(() => {
          this.showShareTooltip = false;
        }, 2000);
      });
    },

  };
}
